import { SyncError } from '@ims/data'

interface ErrorLike {
  message?: string
  code?: string
  status?: number
}

/**
 * Maps a supabase-js / PostgREST error to a SyncError with a stable code.
 * Server RPC errors use the '<code>: <detail>' message convention.
 */
export function toSyncError(error: unknown): SyncError {
  if (error instanceof SyncError) return error
  const e = (error ?? {}) as ErrorLike
  const message = e.message ?? String(error)
  const prefixed = /^([a-z_]+): /.exec(message)
  if (prefixed?.[1]) {
    const code = prefixed[1]
    return new SyncError(code, message.slice(prefixed[0].length), code === 'unauthenticated')
  }
  if (e.code === 'PGRST301' || e.code === 'PGRST303' || e.status === 401 || /JWT/i.test(message)) {
    // Expired session: supabase-js refreshes it, so try again later.
    return new SyncError('unauthenticated', message, true)
  }
  if (e.code === '42501') return new SyncError('forbidden', message, false)
  if (!e.code || /fetch|network|timeout|ECONN|socket/i.test(message) || (e.status ?? 0) >= 500) {
    return new SyncError('network', message, true)
  }
  return new SyncError('error', message, false)
}

/** Unwraps a supabase-js `{ data, error }` result, throwing a SyncError on error. */
export function unwrap<T>(result: { data: T | null; error: unknown }): T {
  if (result.error) throw toSyncError(result.error)
  return result.data as T
}
